import * as React from "react";

import { Input } from "@workspace/ui/components/input";

export interface CountInputProps extends Omit<
	React.ComponentProps<typeof Input>,
	"value" | "onChange" | "type" | "min" | "max"
> {
	value: number;
	onValueChange: (value: number) => void;
	min?: number;
	max?: number;
}

/**
 * A whole-number count, e.g. a tab's covers.
 *
 * HA-457: the field keeps what's being typed as text. It used to turn an emptied field into 0 on
 * the spot, so clearing it and typing 4 showed "04" (the arrow keys never produced that text,
 * which is why they worked). Now an empty field stays empty while it's being edited, and falls
 * back to the minimum only when left.
 */
export function CountInput({
	value,
	onValueChange,
	min = 1,
	max,
	onBlur,
	...props
}: CountInputProps) {
	// The text being edited, or null when the field just shows the value.
	const [draft, setDraft] = React.useState<string | null>(null);

	return (
		<Input
			type="number"
			inputMode="numeric"
			min={min}
			max={max}
			value={draft ?? String(value)}
			onChange={(e) => {
				const text = e.target.value;
				setDraft(text);
				const parsed = Number.parseInt(text, 10);
				if (Number.isFinite(parsed)) onValueChange(parsed);
			}}
			onBlur={(e) => {
				const parsed = Number.parseInt(draft ?? "", 10);
				if (draft !== null && !Number.isFinite(parsed)) onValueChange(min);
				setDraft(null);
				onBlur?.(e);
			}}
			{...props}
		/>
	);
}
